import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { getRecurringExpenses, deleteRecurringExpense } from '../api';
import type { RecurringExpense } from '../api';
import type { ExpenseCategory } from '../types';
import RecurringExpenseModal from '../components/RecurringExpenseModal';
import SortableTh, { type SortDir } from '../components/SortableTh';

type SortKey = 'vehicle' | 'category' | 'description' | 'frequency' | 'next_due' | 'amount';

export default function RecurringPage() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [items, setItems] = useState<RecurringExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterReg, setFilterReg]       = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [editing, setEditing]           = useState<RecurringExpense | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('next_due');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const fmt = (n: number) => formatNumber(n, settings.numberFormat);

  const load = useCallback(async () => {
    setLoading(true);
    setItems(await getRecurringExpenses());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    await deleteRecurringExpense(id);
    setConfirmDelete(null);
    load();
  };

  const filtered = items.filter(r => {
    if (filterReg && !(r.registration_number ?? '').toUpperCase().includes(filterReg.toUpperCase())) return false;
    if (filterCategory && r.category !== filterCategory) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av: string | number = '', bv: string | number = '';
    switch (sortKey) {
      case 'vehicle':     av = a.registration_number ?? '';         bv = b.registration_number ?? ''; break;
      case 'category':    av = t(`expense.category_${a.category}`); bv = t(`expense.category_${b.category}`); break;
      case 'description': av = a.description ?? '';                 bv = b.description ?? ''; break;
      case 'frequency':   av = a.frequency;                         bv = b.frequency; break;
      case 'next_due':    av = a.next_due_date;                     bv = b.next_due_date; break;
      case 'amount':      av = Number(a.amount);                    bv = Number(b.amount); break;
    }
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="page-title">{t('recurring.title')}</h1>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <input type="text" placeholder={t('fleet.filter_reg')} value={filterReg}
          onChange={e => setFilterReg(e.target.value)}
          className="filter-input w-36" />
        <select className="filter-input" value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}>
          <option value="">{t('expense.all_categories')}</option>
          {(['insurance', 'tax', 'cleaning', 'parking', 'other'] as ExpenseCategory[]).map(c => (
            <option key={c} value={c}>{t(`expense.category_${c}`)}</option>
          ))}
        </select>
        {(filterReg || filterCategory) && (
          <button onClick={() => { setFilterReg(''); setFilterCategory(''); }}
            className="text-xs text-ink-500 hover:text-ink-900 ml-1">
            {t('fleet.filter_clear')}
          </button>
        )}
      </div>

      <div className="list-card">
        {loading ? (
          <div className="p-12 text-center text-ink-500">…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-ink-500">{t('recurring.none')}</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh keyName="vehicle"     label={t('fleet.col_vehicle')}        sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="category"    label={t('fleet.col_category')}       sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="description" label={t('fleet.col_description')}    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="frequency"   label={t('expense.frequency_label')}  sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="next_due"    label={t('recurring.next_due')}       sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortableTh keyName="amount"      label={t('fleet.col_amount')}         sortKey={sortKey} sortDir={sortDir} onSort={handleSort} alignRight />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                const overdue = r.next_due_date <= today;
                return (
                  <tr key={r.id} className={overdue ? 'row-warning' : 'row-hover'}>
                    <td className="px-4 py-3">
                      {r.registration_number
                        ? <span className="font-mono text-ink-900">{r.registration_number}</span>
                        : <span className="text-xs text-ink-300">{t('expense.scope_fleet')}</span>}
                    </td>
                    <td className="px-4 py-3 text-ink-700">{t(`expense.category_${r.category}`)}</td>
                    <td className="px-4 py-3 text-ink-700">{r.description || '—'}</td>
                    <td className="px-4 py-3 text-ink-700">{t(`expense.frequency_${r.frequency}`)}</td>
                    <td className="px-4 py-3 text-ink-700">
                      <span className={overdue ? 'text-rose-700 font-medium' : ''}>{r.next_due_date}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-ink-900">{fmt(Number(r.amount))}</td>
                    <td className="px-4 py-3 text-right">
                      {confirmDelete === r.id ? (
                        <span className="flex items-center gap-2 justify-end">
                          <button onClick={() => handleDelete(r.id)} className="text-xs text-rose-700 hover:underline font-medium">{t('expense.delete_confirm')}</button>
                          <button onClick={() => setConfirmDelete(null)} className="text-xs text-ink-300 hover:text-ink-700">&times;</button>
                        </span>
                      ) : (
                        <span className="flex items-center gap-3 justify-end">
                          <button onClick={() => setEditing(r)} className="text-xs text-sage-700 hover:text-sage-900">{t('events.edit_event')}</button>
                          <button onClick={() => setConfirmDelete(r.id)} className="text-xs text-ink-300 hover:text-rose-700">✕</button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <RecurringExpenseModal
          recurring={editing}
          onSaved={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </main>
  );
}
